import React from 'react';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { ShoppingCart, Plus, Minus, X, Banknote, CreditCard, Trash2 } from 'lucide-react';

/**
 * CartDrawer - Slide-in cart for mobile/tablet
 * Swipe/click to close, full cart management
 */
export default function CartDrawer({
  open,
  onOpenChange,
  cart,
  cartTotal,
  onUpdateQuantity,
  onRemoveItem,
  onClearCart,
  onPayCash,
  onPayCard,
  formatCurrency
}) {
  const format = formatCurrency || ((val) => `${val.toFixed(2)}€`);
  
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent 
        side="right" 
        className="w-[92vw] max-w-[400px] bg-[#111C32]/98 backdrop-blur-xl border-l border-slate-700/60 p-0 flex flex-col"
        data-testid="cart-drawer"
      >
        {/* Header */}
        <SheetHeader className="p-4 border-b border-slate-700/50 bg-gradient-to-r from-[#0B1220] to-[#111C32]">
          <SheetTitle className="flex items-center justify-between">
            <span className="flex items-center gap-2 text-white">
              <ShoppingCart size={20} className="text-teal-400" />
              Warenkorb
              <span className="text-sm text-slate-400">
                ({cart.reduce((sum, item) => sum + item.quantity, 0)} Artikel)
              </span>
            </span>
            {cart.length > 0 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={onClearCart}
                className="text-red-400 hover:text-red-300 hover:bg-red-500/10 h-8 px-2"
              >
                <Trash2 size={14} className="mr-1" />
                Leeren
              </Button>
            )}
          </SheetTitle>
        </SheetHeader>
        
        {/* Cart Items */}
        <ScrollArea className="flex-1 p-4">
          {cart.length === 0 ? (
            <div className="text-center py-16 text-slate-500">
              <ShoppingCart size={48} className="mx-auto mb-4 opacity-30" />
              <p className="text-base">Warenkorb ist leer</p>
              <p className="text-sm mt-2 text-slate-600">
                Tippen Sie auf Produkte,<br/>um sie hinzuzufügen
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {cart.map((item, idx) => (
                <div 
                  key={idx} 
                  className="p-4 bg-[#0B1220]/60 rounded-xl border border-slate-700/40 hover:border-slate-600/60 transition-colors"
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex-1 pr-2">
                      <span className="text-base font-medium text-white line-clamp-2">
                        {item.name}
                      </span>
                      {/* Show options if any */}
                      {item.optionsText && (
                        <div className="text-xs text-teal-400 mt-1 line-clamp-2">
                          {item.optionsText}
                        </div>
                      )}
                    </div>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => onRemoveItem(item.name)} 
                      className="h-8 w-8 p-0 text-red-400 hover:text-red-300 hover:bg-red-500/10 shrink-0"
                    >
                      <X size={16} />
                    </Button>
                  </div>
                  
                  <div className="flex items-center justify-between mt-3">
                    {/* Quantity Controls - Bigger touch targets */}
                    <div className="flex items-center gap-2">
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={() => onUpdateQuantity(item.name, -1)} 
                        className="h-10 w-10 p-0 border-slate-600/60 bg-slate-800/50 hover:bg-slate-700/50 text-white"
                      >
                        <Minus size={16} />
                      </Button>
                      <span className="w-10 text-center text-lg font-bold text-white">
                        {item.quantity}
                      </span>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={() => onUpdateQuantity(item.name, 1)} 
                        className="h-10 w-10 p-0 border-slate-600/60 bg-slate-800/50 hover:bg-slate-700/50 text-white"
                      >
                        <Plus size={16} />
                      </Button>
                    </div>
                    
                    {/* Item Total */}
                    <span className="text-teal-400 font-bold text-lg">
                      {format(item.price * item.quantity)}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </ScrollArea>
        
        {/* Footer - Total & Payment */}
        {cart.length > 0 && (
          <div className="p-4 border-t border-slate-700/50 bg-gradient-to-t from-[#0B1220] to-transparent">
            {/* Total */}
            <div className="flex justify-between items-center text-2xl font-bold mb-4">
              <span className="text-white">Gesamt</span>
              <span className="text-teal-400">{format(cartTotal)}</span>
            </div>
            
            {/* Payment Buttons - Redundant in drawer as requested */}
            <div className="grid grid-cols-2 gap-3">
              <Button 
                onClick={() => {
                  onOpenChange(false);
                  onPayCash();
                }} 
                className="h-14 bg-gradient-to-r from-emerald-600 to-emerald-500 hover:from-emerald-500 hover:to-emerald-400 text-white font-bold shadow-lg shadow-emerald-500/20"
                data-testid="drawer-pay-cash"
              >
                <Banknote size={20} className="mr-2" />
                Bar
              </Button>
              <Button 
                onClick={() => {
                  onOpenChange(false);
                  onPayCard();
                }} 
                className="h-14 bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-500 hover:to-blue-400 text-white font-bold shadow-lg shadow-blue-500/20"
                data-testid="drawer-pay-card"
              >
                <CreditCard size={20} className="mr-2" />
                Karte
              </Button>
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
